import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FaSearch, FaSpinner, FaStar, FaRegStar, FaUpload, FaEyeDropper, FaLock, FaUnlock, FaCheck } from 'react-icons/fa';
import { useUser } from '../../../../UserContext'; // Adjust path if needed


export default function EmblemCreator({ onSuccess }) {
  const [view, setView] = useState('main'); // 'main' | 'bg-picker'
  const [loadingFile, setLoadingFile] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  // Settings states
  const [zoom, setZoom] = useState(1);
  const [brightness, setBrightness] = useState(1);
  const [lightAngle, setLightAngle] = useState(360);
  const [lockHorizontal, setLockHorizontal] = useState(true);
  const [emblemName, setEmblemName] = useState('');
  const [isPublic, setIsPublic] = useState(true);
  
  // Background picker states
  const [bgColor, setBgColor] = useState({ r: 255, g: 255, b: 255 });
  const [threshold, setThreshold] = useState(30);

  // Refs for logic & canvas
  const mainCanvasRef = useRef(null);
  const pickerCanvasRef = useRef(null);
  const fileInputRef = useRef(null);
  
  const originalImageRef = useRef(null);
  const processedImageRef = useRef(null);
  const bgImageRef = useRef(null);
  const isDragging = useRef(false);
  const processingCanvasRef = useRef(null);
  
  const emblemParams = useRef({
    imageX: 0,
    imageY: 0,
    imageWidth: 0,
    imageHeight: 0,
    startX: 0,
    startY: 0
  });

  // Load the default empty emblem background on mount
  useEffect(() => {
    const bgImg = new Image();
    bgImg.src = '/images/emblemmaker_minion_empty.png';
    bgImg.onload = () => {
      bgImageRef.current = bgImg;
    };
  }, []);

  // --- CORE DRAWING LOGIC (Translated from legacy) ---
  const drawEmblem = useCallback(() => {
    if (!processedImageRef.current || !bgImageRef.current || !mainCanvasRef.current) return;
    
    const canvas = mainCanvasRef.current;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    const img = processedImageRef.current;
    const bg = bgImageRef.current;
    const params = emblemParams.current;
    const megaOffset = 1.1;

    const angleInRadians = (lightAngle * Math.PI) / 180;
    
    // Shadows config
    const baseOffsetX1 = 3.3 * megaOffset, baseOffsetY1 = 3.3 * megaOffset;
    const baseOffsetX4 = -3.75 * megaOffset, baseOffsetY4 = -3.75 * megaOffset;

    const offsetX1 = baseOffsetX1 * Math.cos(angleInRadians) - baseOffsetY1 * Math.sin(angleInRadians);
    const offsetY1 = baseOffsetX1 * Math.sin(angleInRadians) + baseOffsetY1 * Math.cos(angleInRadians);
    const offsetX4 = baseOffsetX4 * Math.cos(angleInRadians) - baseOffsetY4 * Math.sin(angleInRadians);
    const offsetY4 = baseOffsetX4 * Math.sin(angleInRadians) + baseOffsetY4 * Math.cos(angleInRadians);
    
    const offsetX2 = 0, offsetY2 = 3.5 * megaOffset;
    const offsetX3 = -2.9 * megaOffset, offsetY3 = 2.9 * megaOffset;

    // Build base
    const baseCanvas = document.createElement('canvas');
    baseCanvas.width = 289; baseCanvas.height = 289;
    const baseCtx = baseCanvas.getContext('2d');
    baseCtx.fillStyle = '#dcdcdc';
    baseCtx.fillRect(0, 0, 289, 289);
    baseCtx.globalCompositeOperation = 'destination-atop';
    baseCtx.drawImage(img, params.imageX, params.imageY, params.imageWidth, params.imageHeight);

    // Apply brightness
    const brightCanvas = document.createElement('canvas');
    brightCanvas.width = 289; brightCanvas.height = 289;
    const brightCtx = brightCanvas.getContext('2d');
    brightCtx.filter = `brightness(${brightness})`;
    brightCtx.drawImage(baseCanvas, 0, 0);

    const darkenAmount = Math.min(Math.floor((brightness - 1) * 600), 50);
    const r = Math.max(0, 189 - darkenAmount);
    const indentColor = `rgb(${r},${r},${r})`;
    const lightenAmount = Math.floor((brightness - 1) * 200);
    const newShadowColor = `rgb(${Math.min(255, 244 + lightenAmount)},${Math.min(255, 243 + lightenAmount)},${Math.min(255, 244 + lightenAmount)})`;

    processingCanvasRef.current = brightCanvas;
    const procCtx = brightCanvas.getContext('2d');

    // Init main canvas
    ctx.clearRect(0, 0, 289, 289);
    ctx.drawImage(brightCanvas, 0, 0);

    // Render passes helper
    const renderPass = (color, blur, ox, oy, shadowCol) => {
      ctx.globalCompositeOperation = 'xor';
      ctx.fillStyle = color;
      ctx.fillRect(0, 0, 289, 289);
      ctx.shadowBlur = blur;
      ctx.shadowOffsetX = ox;
      ctx.shadowOffsetY = oy;
      ctx.shadowColor = shadowCol;
      ctx.drawImage(canvas, 0, 0);
      ctx.globalCompositeOperation = 'destination-atop';
      ctx.shadowColor = 'transparent';
      ctx.drawImage(brightCanvas, 0, 0);
      procCtx.clearRect(0, 0, 289, 289);
      procCtx.drawImage(canvas, 0, 0);
    };

    renderPass(indentColor, 2, offsetX1, offsetY1, indentColor);
    renderPass(indentColor, 2, offsetX2, offsetY2, indentColor);
    renderPass(indentColor, 2, offsetX3, offsetY3, indentColor);
    renderPass('#ffffff', 2.5, offsetX4, offsetY4, newShadowColor);

    // Blending
    ctx.globalCompositeOperation = 'xor';
    ctx.fillStyle = '#bdbebd';
    ctx.fillRect(0, 0, 289, 289);
    ctx.globalAlpha = 0.95;
    ctx.globalCompositeOperation = 'color-burn';
    ctx.drawImage(brightCanvas, 0, 0);
    ctx.globalCompositeOperation = 'multiply';
    ctx.drawImage(brightCanvas, 0, 0);
    ctx.globalAlpha = 1;

    const applyMask = (fillColor, alpha, composite1, composite2) => {
      const gradCanvas = document.createElement('canvas');
      gradCanvas.width = 289; gradCanvas.height = 289;
      const gradCtx = gradCanvas.getContext('2d');
      gradCtx.fillStyle = fillColor;
      gradCtx.fillRect(0, 0, 289, 289);
      gradCtx.globalCompositeOperation = 'destination-in';
      gradCtx.drawImage(brightCanvas, 0, 0);
      
      ctx.globalAlpha = alpha;
      ctx.globalCompositeOperation = composite1;
      ctx.drawImage(composite1 === 'color-burn' ? gradCanvas : brightCanvas, 0, 0);
      ctx.globalCompositeOperation = composite2;
      ctx.drawImage(composite1 === 'color-burn' ? brightCanvas : gradCanvas, 0, 0);
      ctx.globalCompositeOperation = 'source-over';
      ctx.globalAlpha = 1;
    };

    applyMask('#585858', 0.68, 'multiply', 'color-dodge');

    // Final composition over BG
    ctx.clearRect(0, 0, 289, 289);
    ctx.drawImage(bg, 253, 35, bg.width, bg.height, 0, 0, bg.width, bg.height);

    applyMask('#d9d9d9', 0.95, 'color-burn', 'multiply');
    applyMask('#585858', 0.68, 'multiply', 'color-dodge');

  }, [brightness, lightAngle]);

  // --- BACKGROUND REMOVAL ---
  const applyBgRemoval = useCallback((imageObj, color, thresh) => {
    const offCanvas = document.createElement('canvas');
    offCanvas.width = imageObj.width; offCanvas.height = imageObj.height;
    const ctx = offCanvas.getContext('2d');
    
    const steps = (offCanvas.width / 289) / 2;
    ctx.filter = `blur(${steps}px)`;
    ctx.drawImage(imageObj, 0, 0);

    const imgData = ctx.getImageData(0, 0, offCanvas.width, offCanvas.height);
    const pix = imgData.data;
    for (let i = 0; i < pix.length; i += 4) {
      if (Math.abs(pix[i] - color.r) <= thresh &&
          Math.abs(pix[i+1] - color.g) <= thresh &&
          Math.abs(pix[i+2] - color.b) <= thresh) {
        pix[i+3] = 0;
      }
    }
    ctx.putImageData(imgData, 0, 0);
    
    const newImg = new Image();
    newImg.src = offCanvas.toDataURL();
    newImg.onload = () => {
      processedImageRef.current = newImg;
      drawEmblem();
    };
  }, [drawEmblem]);

  // Handle Dragging Events
  const handlePointerDown = (e) => {
    if (!processedImageRef.current) return;
    const rect = mainCanvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    emblemParams.current.startX = clientX - rect.left;
    emblemParams.current.startY = clientY - rect.top;
    isDragging.current = true;
  };

  const handlePointerMove = (e) => {
    if (!isDragging.current) return;
    e.preventDefault();
    const rect = mainCanvasRef.current.getBoundingClientRect();
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    const mouseX = clientX - rect.left;
    const mouseY = clientY - rect.top;
    
    const dx = mouseX - emblemParams.current.startX;
    const dy = mouseY - emblemParams.current.startY;
    
    if (!lockHorizontal) emblemParams.current.imageX += dx;
    emblemParams.current.imageY += dy;
    emblemParams.current.startX = mouseX;
    emblemParams.current.startY = mouseY;
    
    drawEmblem(); // Direct redraw for performance
  };

  const handlePointerUp = () => { isDragging.current = false; };

  // Sync zoom changes to parameters
  useEffect(() => {
    if (!processedImageRef.current) return;
    const centerW = emblemParams.current.imageX + (emblemParams.current.imageWidth / 2) || 145;
    const centerH = emblemParams.current.imageY + (emblemParams.current.imageHeight / 2) || 145;
    
    emblemParams.current.imageWidth = processedImageRef.current.width * zoom;
    emblemParams.current.imageHeight = processedImageRef.current.height * zoom;
    emblemParams.current.imageX = centerW - (emblemParams.current.imageWidth / 2);
    emblemParams.current.imageY = centerH - (emblemParams.current.imageHeight / 2);
    drawEmblem();
  }, [zoom, drawEmblem]);

  // Handle Image Upload
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLoadingFile(true);
    const reader = new FileReader();
    reader.onload = (ev) => {
      const img = new Image();
      img.src = ev.target.result;
      img.onload = () => {
        originalImageRef.current = img;
        
        // Auto-scale to fit
        let initScale = 1;
        while (img.height * initScale > 220) { initScale -= 0.0025; }
        setZoom(initScale);
        
        emblemParams.current.imageWidth = img.width * initScale;
        emblemParams.current.imageHeight = img.height * initScale;
        emblemParams.current.imageX = 145 - (emblemParams.current.imageWidth / 2);
        emblemParams.current.imageY = 145 - (emblemParams.current.imageHeight / 2);

        applyBgRemoval(img, bgColor, threshold);
        setLoadingFile(false);
      };
    };
    reader.readAsDataURL(file);
  };

  // BgPicker view logic
  useEffect(() => {
    if (view === 'bg-picker' && originalImageRef.current && pickerCanvasRef.current) {
      const canvas = pickerCanvasRef.current;
      const ctx = canvas.getContext('2d', { willReadFrequently: true });
      const img = originalImageRef.current;
      
      const scale = Math.min(600 / img.width, 400 / img.height, 1);
      canvas.width = Math.floor(img.width * scale);
      canvas.height = Math.floor(img.height * scale);
      
      // Draw image
      ctx.drawImage(img, 0, 0, img.width, img.height, 0, 0, canvas.width, canvas.height);
      
      // Apply live threshold overlay
      const imgData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const pix = imgData.data;
      for (let i = 0; i < pix.length; i += 4) {
        if (Math.abs(pix[i] - bgColor.r) <= threshold &&
            Math.abs(pix[i+1] - bgColor.g) <= threshold &&
            Math.abs(pix[i+2] - bgColor.b) <= threshold) {
          pix[i+3] = 0; // Transparent
        }
      }
      // Create checkerboard bg logic via css, just put data
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.putImageData(imgData, 0, 0);
    }
  }, [view, bgColor, threshold]);

  const handlePickerClick = (e) => {
    if (!originalImageRef.current || !pickerCanvasRef.current) return;
    const rect = pickerCanvasRef.current.getBoundingClientRect();
    const scaleX = pickerCanvasRef.current.width / rect.width;
    const scaleY = pickerCanvasRef.current.height / rect.height;
    const x = Math.floor((e.clientX - rect.left) * scaleX);
    const y = Math.floor((e.clientY - rect.top) * scaleY);
    
    // Draw raw image temp to get exact pixel color
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = pickerCanvasRef.current.width;
    tempCanvas.height = pickerCanvasRef.current.height;
    const tempCtx = tempCanvas.getContext('2d');
    tempCtx.drawImage(originalImageRef.current, 0, 0, tempCanvas.width, tempCanvas.height);
    
    const data = tempCtx.getImageData(x, y, 1, 1).data;
    setBgColor({ r: data[0], g: data[1], b: data[2] });
  };

  const submitEmblem = () => {
    if (!processingCanvasRef.current || !emblemName.trim()) return alert("Please upload an image and provide a name.");
    setIsUploading(true);
    
    processingCanvasRef.current.toBlob((blob) => {
      const formData = new FormData();
      formData.append('file', blob, 'file.png');
      formData.append('emblemParams', JSON.stringify(emblemParams.current));
      formData.append('emblemName', emblemName);
      formData.append('emblemPublic', isPublic);

      fetch('/ajax/emblem_recieve.php', { method: "POST", body: formData })
        .then(res => res.text())
        .then(() => {
          setIsUploading(false);
          onSuccess(); // Switch to My Assets
        })
        .catch(err => {
          console.error("Upload error:", err);
          setIsUploading(false);
        });
    });
  };

  if (view === 'bg-picker') {
    return (
      <div className="d-flex flex-column h-100 p-4 text-center">
        <h5 className="text-warning mb-3">Select Background Color to Remove</h5>
        <p className="small text-muted mb-3">Click on the image below to pick the background color. Adjust the threshold to include variations.</p>
        
        <div className="flex-grow-1 overflow-auto d-flex justify-content-center align-items-center bg-black rounded border border-secondary p-2 mb-3">
           <canvas 
             ref={pickerCanvasRef} 
             onClick={handlePickerClick} 
             className="cursor-crosshair bg-checkerboard"
             style={{ backgroundImage: 'repeating-conic-gradient(#808080 0% 25%, transparent 0% 50%)', backgroundSize: '20px 20px', backgroundPosition: '0 0, 10px 10px' }}
           />
        </div>
        
        <div className="row g-3 align-items-center mb-3">
          <div className="col-auto">
             <div className="d-flex align-items-center border border-secondary p-1 rounded">
               <span className="me-2 text-muted small">Picked:</span>
               <div style={{ width: '30px', height: '30px', backgroundColor: `rgb(${bgColor.r},${bgColor.g},${bgColor.b})`, border: '1px solid #fff' }}></div>
             </div>
          </div>
          <div className="col text-start">
             <label className="form-label small mb-0">Threshold: {threshold}</label>
             <input type="range" className="form-range custom-range-warning" min="0" max="100" value={threshold} onChange={(e) => setThreshold(e.target.value)} />
          </div>
        </div>
        
        <div className="d-flex justify-content-end gap-2">
          <button className="btn btn-outline-secondary" onClick={() => { setView('main'); applyBgRemoval(originalImageRef.current, bgColor, threshold); }}>Cancel</button>
          <button className="btn btn-warning" onClick={() => { setView('main'); applyBgRemoval(originalImageRef.current, bgColor, threshold); }}><FaCheck className="me-1" /> Confirm Selection</button>
        </div>
      </div>
    );
  }

  return (
    <div className="row g-0 h-100 p-3 p-md-4" style={{ overflowY: 'auto' }}>
      {/* LEFT COLUMN: Controls */}
      <div className="col-12 col-md-7 pe-md-4 d-flex flex-column gap-3">
        
        <div>
          <label className="form-label text-warning fw-bold small text-uppercase">1. Choose File</label>
          <input type="file" ref={fileInputRef} className="form-control bg-dark text-white border-secondary mb-2" accept="image/*" onChange={handleFileChange} />
          <p className="small text-muted mb-0">
            Use simple shapes with transparent or solid background. Best results with thick lines and clear negative space.
          </p>
        </div>

        <div className="bg-black bg-opacity-25 border border-secondary rounded p-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
             <label className="form-label text-warning fw-bold small text-uppercase mb-0">2. Adjust Appearance</label>
             <button 
               className="btn btn-sm btn-outline-light d-flex align-items-center" 
               onClick={() => setView('bg-picker')}
               disabled={!originalImageRef.current}
             >
               <FaEyeDropper className="me-1" /> Remove BG Color
             </button>
          </div>

          <label className="form-label small mb-0 mt-2">Resize (Zoom)</label>
          <input type="range" className="form-range" min="0.1" max="3" step="0.0025" value={zoom} onChange={e => setZoom(parseFloat(e.target.value))} />

          <label className="form-label small mb-0 mt-2">Brightness</label>
          <input type="range" className="form-range" min="1" max="1.015" step="0.001" value={brightness} onChange={e => setBrightness(parseFloat(e.target.value))} />
          
          <label className="form-label small mb-0 mt-2">Light Direction</label>
          <input type="range" className="form-range" min="305" max="415" value={lightAngle} onChange={e => setLightAngle(e.target.value)} />
          
          <div className="form-check mt-3">
             <input type="checkbox" className="form-check-input bg-dark border-secondary" id="lockH" checked={lockHorizontal} onChange={e => setLockHorizontal(e.target.checked)} />
             <label className="form-check-label small text-muted cursor-pointer" htmlFor="lockH">
               {lockHorizontal ? <FaLock className="me-1 text-warning"/> : <FaUnlock className="me-1"/>} Lock Horizontal Dragging
             </label>
          </div>
        </div>

        <div>
          <label className="form-label text-warning fw-bold small text-uppercase mb-2">3. Finalize</label>
<div className="input-group">
  <input
    type="text"
    className="form-control bg-dark text-white border-secondary"
    placeholder="Name your emblem"
    value={emblemName}
    onChange={e => setEmblemName(e.target.value)}
  />

  <div className="input-group-text bg-dark border-secondary">
    <input
      className="form-check-input mt-0 me-2"
      type="checkbox"
      checked={isPublic}
      onChange={e => setIsPublic(e.target.checked)}
    />
    Public
  </div>
</div>
        </div>

      </div>

      {/* RIGHT COLUMN: Canvas & Upload */}
      <div className="col-12 col-md-5 d-flex flex-column align-items-center justify-content-start border-start border-secondary ps-md-4 mt-4 mt-md-0">
        
        <div className="position-relative w-100 d-flex justify-content-center align-items-center bg-black bg-opacity-50 rounded border border-secondary py-4 mb-3" style={{ touchAction: 'none' }}>
           {!originalImageRef.current && !loadingFile && (
             <div className="position-absolute text-muted small px-4 text-center pointer-events-none">
                Upload an image to see the preview here. You can drag it to position.
             </div>
           )}
           {loadingFile && <FaSpinner className="fa-spin fa-2x text-warning position-absolute" />}
           
           <canvas 
             ref={mainCanvasRef} 
             width="289" height="289" 
             className="border border-dark shadow-sm cursor-move"
             style={{ maxWidth: '100%', height: 'auto', opacity: loadingFile ? 0 : 1 }}
             onMouseDown={handlePointerDown}
             onMouseMove={handlePointerMove}
             onMouseUp={handlePointerUp}
             onMouseLeave={handlePointerUp}
             onTouchStart={handlePointerDown}
             onTouchMove={handlePointerMove}
             onTouchEnd={handlePointerUp}
           />
        </div>

        <button 
          className="btn btn-warning w-100 fw-bold py-2 shadow-sm d-flex justify-content-center align-items-center" 
          onClick={submitEmblem}
          disabled={!originalImageRef.current || isUploading || !emblemName.trim()}
        >
          {isUploading ? <FaSpinner className="fa-spin me-2" /> : <FaUpload className="me-2" />}
          {isUploading ? 'Uploading...' : 'Upload Emblem'}
        </button>

      </div>
    </div>
  );
}